import { useEffect, useState } from "react";
import type { MeetupDate, Post } from "@/data/demo";
import { dates as dateFallback, posts as postFallback } from "@/data/demo";
import { api } from "./api";

export type AccountProfile = {
  email: string;
  account_type: string;
  locality: string;
  profile: { display_name: string; introduction?: string; visibility: string };
};
export type ConversationSummary = { id: number; name: string; preview: string };
export type LiveMessage = {
  id: number;
  body: string;
  sender_user_id: number;
  created_at: string;
};

type ApiPost = {
  id: number;
  slug: string;
  title: string;
  description: string;
  category: string;
  poster_type?: string;
  seeking?: string[];
  location_label: string;
  published_at?: string;
  distance_km?: number | string | null;
  owner?: {
    id: number;
    account_type: string;
    profile?: { display_name: string };
  };
};
type ApiDate = {
  slug: string;
  title: string;
  description?: string;
  status: string;
  starts_at: string;
  location_label: string;
  location_privacy: string;
  capacity: number;
  accepted_count?: number;
  distance_km?: number | string | null;
};

export type GeoSearch = {
  latitude: number | null;
  longitude: number | null;
  radius: number;
};

function post(item: ApiPost): Post {
  const owner = item.owner?.profile?.display_name ?? "Krosspaths member";
  return {
    id: item.slug,
    databaseId: item.id,
    ownerId: item.owner?.id,
    title: item.title,
    description: item.description,
    owner,
    initials: owner
      .split(/\s|&/)
      .filter(Boolean)
      .slice(0, 2)
      .map((v) => v[0])
      .join("")
      .toUpperCase(),
    accountType:
      item.owner?.account_type === "shared_couple" ? "Couple" : "Single",
    location: item.location_label,
    distance:
      item.distance_km !== null && item.distance_km !== undefined
        ? `${Math.max(0.1, Number(item.distance_km)).toFixed(1)} km`
        : "nearby",
    category: item.category,
    posterType: item.poster_type ?? "Member",
    seeking: item.seeking ?? ["Any"],
    freshness: item.published_at
      ? new Intl.RelativeTimeFormat("en", { numeric: "auto" }).format(
          -Math.max(
            1,
            Math.round(
              (Date.now() - new Date(item.published_at).getTime()) / 3600000,
            ),
          ),
          "hour",
        )
      : "recently",
    accent: "warm",
  };
}
function date(item: ApiDate): MeetupDate {
  const starts = new Date(item.starts_at);
  const remaining = Math.max(0, item.capacity - (item.accepted_count ?? 0));
  return {
    id: item.slug,
    day: starts.toLocaleDateString("en-AU", { day: "2-digit" }),
    month: starts.toLocaleDateString("en-AU", { month: "short" }),
    weekday: starts.toLocaleDateString("en-AU", { weekday: "long" }),
    time: starts.toLocaleTimeString("en-AU", {
      hour: "numeric",
      minute: "2-digit",
    }),
    title: item.title,
    location: `${item.location_label} · ${item.location_privacy === "hidden" ? "details shared after acceptance" : "location shown"}`,
    places: `${remaining} ${remaining === 1 ? "place" : "places"} remaining`,
    status:
      item.status === "invitation_only"
        ? "Invitation only"
        : remaining <= 1
          ? "Almost full"
          : "Open",
    description: item.description,
  };
}

export function usePosts(query = "", category = "", geo?: GeoSearch, posterType = "", seeking = "") {
  const key = `${category}\u0000${posterType}\u0000${seeking}\u0000${query}\u0000${geo?.latitude ?? ""}\u0000${geo?.longitude ?? ""}\u0000${geo?.radius ?? ""}`;
  const [result, setResult] = useState<{ key: string; items: Post[] } | null>(
    null,
  );
  useEffect(() => {
    let active = true;
    api
      .get("/posts", {
        params: {
          q: query,
          category,
          poster_type: posterType || undefined,
          seeking: seeking || undefined,
          latitude: geo?.latitude ?? undefined,
          longitude: geo?.longitude ?? undefined,
          radius_km: geo?.radius,
        },
      })
      .then(({ data }) => {
        if (active) setResult({ key, items: (data.data ?? []).map(post) });
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [query, category, posterType, seeking, key, geo?.latitude, geo?.longitude, geo?.radius]);
  if (result?.key === key) return result.items;
  const term = query.trim().toLowerCase();
  return postFallback.filter(
    (item) =>
      (!category || item.category.toLowerCase() === category.toLowerCase()) &&
      (!posterType || item.posterType === posterType) &&
      (!seeking || item.seeking.includes(seeking) || item.seeking.includes("Any")) &&
      (!term ||
        `${item.title} ${item.description} ${item.location}`
          .toLowerCase()
          .includes(term)),
  );
}
export function useDates(query = "", geo?: GeoSearch) {
  const [items, setItems] = useState<MeetupDate[]>(dateFallback);
  useEffect(() => {
    let active = true;
    api
      .get("/dates", {
        params: {
          q: query,
          latitude: geo?.latitude ?? undefined,
          longitude: geo?.longitude ?? undefined,
          radius_km: geo?.radius,
        },
      })
      .then(({ data }) => {
        if (active) setItems((data.data ?? []).map(date));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [query, geo?.latitude, geo?.longitude, geo?.radius]);
  return items;
}
export function usePost(slug?: string) {
  const fallback =
    postFallback.find((item) => item.id === slug) ?? postFallback[0];
  const [item, setItem] = useState(fallback);
  useEffect(() => {
    if (!slug) return;
    api
      .get(`/posts/${slug}`)
      .then(({ data }) => setItem(post(data.data)))
      .catch(() => {});
  }, [slug]);
  return item;
}
export function useDate(slug?: string) {
  const fallback =
    dateFallback.find((item) => item.id === slug) ?? dateFallback[0];
  const [item, setItem] = useState(fallback);
  useEffect(() => {
    if (!slug) return;
    api
      .get(`/dates/${slug}`)
      .then(({ data }) => setItem(date(data.data)))
      .catch(() => {});
  }, [slug]);
  return item;
}

export function useAccountProfile() {
  const [profile, setProfile] = useState<AccountProfile | null>(null);
  useEffect(() => {
    api
      .get("/account/profile")
      .then(({ data }) => setProfile(data.data))
      .catch(() => {});
  }, []);
  return profile;
}

export function useConversations() {
  const [items, setItems] = useState<ConversationSummary[] | null>(null);
  useEffect(() => {
    Promise.all([api.get("/auth/session"), api.get("/conversations")])
      .then(([session, response]) => {
        const me = session.data.data.id;
        setItems(
          (response.data.data ?? []).map(
            (item: {
              id: number;
              participants: Array<{
                id: number;
                profile?: { display_name: string };
              }>;
              messages?: Array<{ body?: string }>;
            }) => ({
              id: item.id,
              name:
                item.participants.find((person) => person.id !== me)?.profile
                  ?.display_name ?? "Conversation",
              preview: item.messages?.[0]?.body ?? "Conversation started",
            }),
          ),
        );
      })
      .catch(() => {});
  }, []);
  return items;
}

export function useMessages(id?: string) {
  const numeric = Number(id);
  const [messages, setMessages] = useState<LiveMessage[] | null>(null);
  const [myId, setMyId] = useState<number | null>(null);
  useEffect(() => {
    if (!Number.isInteger(numeric)) return;
    let active = true;
    let last = 0;
    api
      .get("/auth/session")
      .then(({ data }) => setMyId(data.data.id))
      .catch(() => {});
    const poll = () =>
      api
        .get(`/conversations/${numeric}/messages`, {
          params: { after_id: last },
        })
        .then(({ data }) => {
          if (!active) return;
          const incoming = data.data ?? [];
          if (incoming.length) {
            last = data.poll_after_id;
            setMessages((current) => [...(current ?? []), ...incoming]);
          } else setMessages((current) => current ?? []);
        })
        .catch(() => {});
    poll();
    const timer = window.setInterval(poll, 5000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [numeric]);
  return { messages, myId };
}
